/**
 * Shared Acquired Book Page
 *
 * Public page that displays acquired book performance metrics via share token.
 * No authentication required - token provides access control.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Loader2, AlertCircle, BookOpen, Library, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, DollarSign, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { formatCompactCurrency, formatCurrency } from '../utils/formatters';
import { AcquiredBookMetrics } from '../hooks/useAcquiredBookMetrics';
import RetentionTrendChart from '../components/refinance/RetentionTrendChart';

/** Compact currency from cents, the app-wide three-significant-figure rule. */
const formatCurrencyCompact = (cents: number): string => formatCompactCurrency(cents);

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, subValue, icon, color, trend }) => (
  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      {trend && (
        <div className={`p-1 rounded ${
          trend === 'up' ? 'bg-green-900/30' :
          trend === 'down' ? 'bg-red-900/30' : 'bg-gray-700'
        }`}>
          {trend === 'up' && <TrendingUp className="w-4 h-4 text-green-400" />}
          {trend === 'down' && <TrendingDown className="w-4 h-4 text-red-400" />}
          {trend === 'neutral' && <Minus className="w-4 h-4 text-gray-400" />}
        </div>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
    </div>
  </div>
);

const SharedAcquiredBookPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [metrics, setMetrics] = useState<AcquiredBookMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedMetrics = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const getSharedMetricsFn = httpsCallable<
          { token: string },
          { success: boolean; metrics: AcquiredBookMetrics }
        >(functions, 'getSharedAcquiredBookMetrics');

        const response = await getSharedMetricsFn({ token });

        if (response.data.success) {
          setMetrics(response.data.metrics);
        } else {
          setError('Failed to load metrics');
        }
      } catch (err: any) {
        console.error('Failed to load shared metrics:', err);

        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Data not found');
        } else {
          setError(err.message || 'Failed to load shared metrics');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedMetrics();
  }, [token]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading acquired book performance...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div>
        <PageHeader
          icon={Library}
          title="Acquired Book Performance"
          subtitle={`${metrics.acquiredTemplateName} → ${metrics.primaryTemplateName} | Shared Report - Read Only`}
        />

        <div className="px-8 pb-8">
        {/* Metrics Content */}
        <div className="space-y-6">
          {/* Summary Cards - Row 1: Counts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Discharged"
              value={metrics.totalDischargesFromBook.toLocaleString()}
              subValue="loans from acquired book"
              icon={<TrendingDown className="w-4 h-4 text-gray-400" />}
              color="bg-gray-700"
            />
            <MetricCard
              title="Retained to Primary"
              value={`${metrics.retainedConfirmed} ✓`}
              subValue={metrics.retainedPending > 0 ? `+${metrics.retainedPending} pending verification` : 'all verified'}
              icon={<TrendingUp className="w-4 h-4 text-green-400" />}
              color="bg-green-900/30"
              trend="up"
            />
            <MetricCard
              title="Lost to External"
              value={metrics.lostToExternal.toLocaleString()}
              subValue="loans not retained"
              icon={<AlertTriangle className="w-4 h-4 text-red-400" />}
              color="bg-red-900/30"
              trend="down"
            />
            <MetricCard
              title="Retention Rate"
              value={`${metrics.retentionRate}%`}
              subValue={`${metrics.matchedDischargedLoans || 0} of ${metrics.totalDischargesFromBook} discharges refinanced → ${metrics.retainedToPrimary} new loans`}
              icon={<TrendingUp className="w-4 h-4 text-blue-400" />}
              color="bg-blue-900/30"
              trend={metrics.retentionRate >= 50 ? 'up' : metrics.retentionRate >= 25 ? 'neutral' : 'down'}
            />
          </div>

          {/* Summary Cards - Row 2: Loan Value Metrics + Trail Book Health */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Discharged Value"
              value={formatCurrency(metrics.dischargedValueCents)}
              subValue="loan value at discharge"
              icon={<DollarSign className="w-4 h-4 text-gray-400" />}
              color="bg-gray-700"
            />
            <MetricCard
              title="Total Refinanced Value"
              value={formatCurrency(metrics.retainedLoanAmountCents)}
              subValue="new loan values in primary book"
              icon={<DollarSign className="w-4 h-4 text-green-400" />}
              color="bg-green-900/30"
              trend="up"
            />
            {(() => {
              const loanValueGain = metrics.retainedLoanAmountCents - metrics.dischargedValueCents;
              const percentChange = metrics.dischargedValueCents > 0
                ? ((loanValueGain / metrics.dischargedValueCents) * 100).toFixed(1)
                : 0;
              const isPositive = loanValueGain >= 0;
              return (
                <MetricCard
                  title="Loan Value Gain"
                  value={`${isPositive ? '+' : ''}${formatCurrency(loanValueGain)}`}
                  subValue={`${isPositive ? '+' : ''}${percentChange}% vs discharged`}
                  icon={isPositive ? <ArrowUpRight className="w-4 h-4 text-green-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                  color={isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                  trend={isPositive ? 'up' : 'down'}
                />
              );
            })()}
            {/* Trail Book Health: Rolling 3-run comparison for accurate trend analysis */}
            {metrics.firstRunMonth && metrics.latestRunMonth && metrics.firstRunMonth !== metrics.latestRunMonth ? (
              (() => {
                const trailChange = metrics.trailChangePercent;
                const isPositive = trailChange > 0;
                const isNeutral = trailChange === 0;
                const isNegative = trailChange < 0;
                const firstAmount = formatCurrencyCompact(metrics.firstRunTrailCents);
                const latestAmount = formatCurrencyCompact(metrics.latestRunTrailCents);
                return (
                  <MetricCard
                    title="Trail Book Change (3-run)"
                    value={`${isPositive ? '+' : ''}${trailChange}%`}
                    subValue={`${metrics.firstRunMonth} ${firstAmount} → ${metrics.latestRunMonth} ${latestAmount}`}
                    icon={
                      isNeutral
                        ? <Minus className="w-4 h-4 text-gray-400" />
                        : isNegative
                          ? <TrendingDown className="w-4 h-4 text-red-400" />
                          : <TrendingUp className="w-4 h-4 text-green-400" />
                    }
                    color={isNeutral ? 'bg-gray-700' : isNegative ? 'bg-red-900/30' : 'bg-green-900/30'}
                    trend={isNeutral ? 'neutral' : isNegative ? 'down' : 'up'}
                  />
                );
              })()
            ) : (
              <MetricCard
                title="Trail Book Change (3-run)"
                value="N/A"
                subValue="Not enough data yet"
                icon={<Minus className="w-4 h-4 text-gray-400" />}
                color="bg-gray-700"
              />
            )}
          </div>

          {/* Summary Cards - Row 3: Commission + Trail Comparison Metrics */}
          {(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Upfront Commission"
                value={formatCurrency(metrics.retainedCommissionCents)}
                subValue={`from ${metrics.retainedToPrimary} retained loans`}
                icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                color="bg-green-900/30"
                trend="up"
              />
              <MetricCard
                title="Previous Avg Monthly Trail"
                value={formatCurrency(metrics.previousAvgMonthlyTrailCents)}
                subValue="monthly trail before discharge"
                icon={<DollarSign className="w-4 h-4 text-purple-400" />}
                color="bg-purple-900/30"
              />
              <MetricCard
                title="New Avg Monthly Trail"
                value={formatCurrency(metrics.newAvgMonthlyTrailCents)}
                subValue="monthly trail after refinance"
                icon={<DollarSign className="w-4 h-4 text-cyan-400" />}
                color="bg-cyan-900/30"
              />
              {(() => {
                const trailDelta = metrics.trailDeltaCents;
                const isPositive = trailDelta >= 0;
                const isNeutral = trailDelta === 0;
                return (
                  <MetricCard
                    title="Net Trail Impact"
                    value={`${isPositive && !isNeutral ? '+' : ''}${formatCurrency(trailDelta)}`}
                    subValue={isNeutral ? 'no change' : `${isPositive ? 'gained' : 'reduced'} per month`}
                    icon={
                      isNeutral
                        ? <Minus className="w-4 h-4 text-gray-400" />
                        : isPositive
                          ? <ArrowUpRight className="w-4 h-4 text-green-400" />
                          : <ArrowDownRight className="w-4 h-4 text-red-400" />
                    }
                    color={isNeutral ? 'bg-gray-700' : isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                    trend={isNeutral ? 'neutral' : isPositive ? 'up' : 'down'}
                  />
                );
              })()}
            </div>
          )}

          {/* Fallback: Show commission card alone if no trail data */}
          {!(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <MetricCard
                title="Total Upfront Commission"
                value={formatCurrency(metrics.retainedCommissionCents)}
                subValue={`from ${metrics.retainedToPrimary} retained loans`}
                icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                color="bg-green-900/30"
                trend="up"
              />
            </div>
          )}

          {/* Book Flow Visualization */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Loan Book Flow</h3>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="bg-purple-900/30 border border-purple-700 rounded-lg px-4 py-3 text-center">
                <p className="text-purple-300 text-sm">{metrics.acquiredTemplateName}</p>
                <p className="text-white font-bold">{metrics.totalDischargesFromBook} discharged</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-green-400 text-sm">{metrics.retentionRate}% retained</div>
                <div className="w-20 h-0.5 bg-gradient-to-r from-purple-500 to-green-500" />
                <div className="text-gray-400 text-sm">{100 - metrics.retentionRate}% lost</div>
              </div>
              <div className="bg-green-900/30 border border-green-700 rounded-lg px-4 py-3 text-center">
                <p className="text-green-300 text-sm">{metrics.primaryTemplateName}</p>
                <p className="text-white font-bold">{metrics.retainedConfirmed} ✓ retained</p>
                {metrics.retainedPending > 0 && (
                  <p className="text-yellow-400 text-xs">+{metrics.retainedPending} pending</p>
                )}
              </div>
            </div>
          </div>

          {/* Monthly Trend Chart */}
          {metrics.monthlyTrends.length > 0 && (
            <RetentionTrendChart
              data={metrics.monthlyTrends}
              title="Monthly Retention Trend"
              viewMode="combined"
            />
          )}

          {/* Yearly Summary Table */}
          {metrics.yearlySummary.length > 0 && (
            <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
              <h3 className="text-lg font-medium text-white px-4 py-3 border-b border-gray-700">
                Yearly Summary
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900">
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Year</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Discharged</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Retained (✓/⏳)</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Lost</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Rate</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Upfront Commission</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Loan Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.yearlySummary.map((year) => (
                      <tr key={year.year} className="border-t border-gray-700 hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-white font-medium">{year.year}</td>
                        <td className="px-4 py-3 text-right text-gray-300">{year.discharges.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-green-400">{year.retainedConfirmed}✓</span>
                          {year.retainedPending > 0 && (
                            <span className="text-yellow-400 ml-1">+{year.retainedPending}⏳</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-red-400">{year.lost.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            year.retentionRate >= 50 ? 'bg-green-900/50 text-green-300' :
                            year.retentionRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                            'bg-red-900/50 text-red-300'
                          }`}>
                            {year.retentionRate}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-green-400">
                          {formatCurrency(year.retainedCommissionCents)}
                        </td>
                        <td className="px-4 py-3 text-right text-cyan-400">
                          {formatCurrency(year.retainedLoanAmountCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {(() => {
                      // Use pre-computed metrics totals instead of summing yearly data
                      // Yearly sums can double-count loans that appear in multiple years
                      const totals = {
                        discharges: metrics.totalDischargesFromBook,
                        retainedConfirmed: metrics.retainedConfirmed,
                        retainedPending: metrics.retainedPending,
                        lost: metrics.lostToExternal,
                        commissionCents: metrics.retainedCommissionCents,
                        loanValueCents: metrics.retainedLoanAmountCents,
                      };
                      const overallRate = metrics.retentionRate;
                      return (
                        <tr className="border-t-2 border-gray-600 bg-gray-900 font-semibold">
                          <td className="px-4 py-3 text-white">Total</td>
                          <td className="px-4 py-3 text-right text-gray-200">{totals.discharges.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-green-400">{totals.retainedConfirmed}✓</span>
                            {totals.retainedPending > 0 && (
                              <span className="text-yellow-400 ml-1">+{totals.retainedPending}⏳</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-red-400">{totals.lost.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              overallRate >= 50 ? 'bg-green-900/50 text-green-300' :
                              overallRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                              'bg-red-900/50 text-red-300'
                            }`}>
                              {overallRate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-green-400">
                            {formatCurrency(totals.commissionCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-cyan-400">
                            {formatCurrency(totals.loanValueCents)}
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center py-6 text-slate-500 text-sm">
            <p>This is a shared, read-only view of the acquired book performance report.</p>
            <p className="mt-1">
              Powered by{' '}
              <a href="https://valutten.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">
                VALUTTEN
              </a>
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SharedAcquiredBookPage;
